// Traveler guides: a real trip by an app user, published by Andiamo as a
// reviewed COPY of the author's guide (the original is never changed). The
// copy is marked guide_info.type = "Traveler" plus a `traveler` record
// (author, trip dates, source guide, status). The cleanup comes from a plan
// file built offline from the review (Traveler guides/<name>-plan.json):
//   { source_guide_info_id, guide_name, description, language, image, image_m,
//     traveler: { author_uid, author_name, trip_dates },
//     drop_schedules: ["…"], drop_places: ["id"],
//     places: { id: { …fields to set } }, events: { id: { …fields to set } } }
// Used by Admin Dashboard → Guides (TravelerGuidePanel). 2026-10-27.

const scheduleOf = (e) => (typeof e.Itinerary === "string" && e.Itinerary !== "" ? e.Itinerary : "Main");

// Returns a copy of the guide's data with the plan applied; input unchanged.
export function applyTravelerPlan(data, plan) {
  const dropSchedules = new Set(plan.drop_schedules || []);
  const dropPlaces = new Set((plan.drop_places || []).map(String));
  const events = (data.schedule?.events || [])
    .filter((e) => !dropSchedules.has(scheduleOf(e)))
    .map((e) => (plan.events?.[e.ID] ? { ...e, ...plan.events[e.ID] } : e))
    .map((e) => {
      const ids = String(e.Places || "").split(",").map((s) => s.trim()).filter((id) => id && !dropPlaces.has(id));
      return ids.join(",") === String(e.Places || "") ? e : { ...e, Places: ids.join(",") };
    });
  const placesList = (data.places?.placesList || [])
    .filter((p) => !dropPlaces.has(String(p.ID)))
    .map((p) => (plan.places?.[String(p.ID)] ? { ...p, ...plan.places[String(p.ID)] } : p));
  const out = { ...data, places: { ...data.places, placesList } };
  if (data.schedule) out.schedule = { ...data.schedule, events };
  // private trip records never go into a published copy
  ["flights", "expenses", "tripMembers"].forEach((k) => {
    if (out[k] != null) out[k] = Array.isArray(out[k]) ? [] : {};
  });
  return out;
}

// What the copy will look like, for the panel's Check step.
export function summarizeTravelerPlan(data, plan) {
  const after = applyTravelerPlan(data, plan);
  const count = (d) => ({ places: (d.places?.placesList || []).length, events: (d.schedule?.events || []).length });
  const schedules = [...new Set((after.schedule?.events || []).map(scheduleOf))];
  return { before: count(data), after: count(after), schedules, placesChanged: Object.keys(plan.places || {}).length, eventsChanged: Object.keys(plan.events || {}).length };
}

// guide_info for the copy: catalog fields from the source, then the plan's
// title/description/cover, the Traveler marking, fresh counters, and draft
// status. Owner fields (user_id, owner_name, guide_id) are set on save.
export function travelerInfo(sourceInfo, plan, nowIso) {
  const info = { ...sourceInfo };
  ["rating", "num_ratings", "times_read", "times_copied", "guide_id", "user_id", "owner_name", "last_modified_by", "access"].forEach((k) => delete info[k]);
  return {
    ...info,
    guide_name: plan.guide_name || sourceInfo.guide_name,
    description: plan.description ?? sourceInfo.description,
    language: plan.language || sourceInfo.language,
    image: plan.image || sourceInfo.image,
    image_m: plan.image_m || plan.image || sourceInfo.image_m,
    type: "Traveler",
    traveler: {
      ...(plan.traveler || {}),
      source_guide_info_id: plan.source_guide_info_id,
      status: "draft",
      version: 1,
      created: nowIso,
    },
    rating: 0,
    num_ratings: 0,
    times_read: 0,
    times_copied: 0,
    created: nowIso,
    mod: nowIso,
    read: nowIso,
    copy: nowIso,
  };
}
